import {GroupShop} from 'ApplicationEntry/models/ProductsRiders/Shop/GroupShop';
import {NewYorkShop} from 'ApplicationEntry/models/ProductsRiders/Shop/NewYorkShop';
import {OrdShop} from 'ApplicationEntry/models/ProductsRiders/Shop/OrdinaryShop';
import * as RI from '../RiderInformation';

export type Shop = OrdShop | GroupShop | NewYorkShop;

type ExhaustiveShopOptionsByType<TReturn> = {
  Ordinary: (ordinary: OrdShop) => TReturn;
  Group: (group: GroupShop) => TReturn;
  NewYork: (newYork: NewYorkShop) => TReturn;
};

type PartialShopOptionsByType<TReturn> = {
  Ordinary?: (ordinary: OrdShop) => TReturn;
  Group?: (group: GroupShop) => TReturn;
  NewYork?: (newYork: NewYorkShop) => TReturn;
  _: (app: Shop) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: ShopByTypeOptions<TReturn>
): options is ExhaustiveShopOptionsByType<TReturn> => !!options.Ordinary || (!!options.Group && !!options.NewYork);

type ShopByTypeOptions<TReturn> = PartialShopOptionsByType<TReturn> | ExhaustiveShopOptionsByType<TReturn>;

export const matchShopByType = <TReturn>(options: ShopByTypeOptions<TReturn>) => (app: Shop): TReturn => {
  switch (app.key) {
    case 'InvalidOrdShop':
    case 'ValidOrdShop':
      return isExhaustiveByType(options)
        ? options.Ordinary(app)
        : options.Ordinary
        ? options.Ordinary(app)
        : options._(app);
    case 'InvalidGroupShop':
    case 'ValidGroupShop':
      return isExhaustiveByType(options) ? options.Group(app) : options.Group ? options.Group(app) : options._(app);
    case 'InvalidNewYorkShop':
    case 'ValidNewYorkShop':
      return isExhaustiveByType(options)
        ? options.NewYork(app)
        : options.NewYork
        ? options.NewYork(app)
        : options._(app);
  }
};

export const isValidShop = (shop: Shop) =>
  shop.key === 'ValidOrdShop' || shop.key === 'ValidGroupShop' || shop.key === 'ValidNewYorkShop';

export const groupShop1Units = ['Hosp Rel', 'Surg', 'Out', 'Diag/Well', 'Presc', 'Disa', 'Life'];
export const groupShop1Amounts = [2, 1, 1, 1, 1, 1, 1];
export const groupShop2Units = ['Initial/1st day', 'Daily Hospitalization', 'Intensive Care'];
export const groupShop2Amounts = [1, 1, 1];
export const groupPlanCodes = ['GVSP1', 'GVSP2'];
export const getAllRiderCodes = (plan: string) => {
  if (plan === groupPlanCodes[0]) {
    return groupRiderCodes1;
  } else if (plan === groupPlanCodes[1]) {
    return groupRiderCodes2;
  }
  return [];
};

export const groupRiderCodes1 = [
  {
    code: 'HOSP',
    propertyName: 'hospitalization',
    displayName: 'Hosp Rel',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'SURG',
    propertyName: 'surgicalBenefits',
    displayName: 'Surg',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'OUTP',
    propertyName: 'outpatient',
    displayName: 'Out',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'DIAG',
    propertyName: 'diagnosticWellness',
    displayName: 'Diag/Well',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'PRES',
    propertyName: 'prescription',
    displayName: 'Presc',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'STDB',
    propertyName: 'stdRider',
    displayName: 'Disa',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'LIFE',
    propertyName: 'termLife',
    displayName: 'Life',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'Continuation of Coverage',
    propertyName: 'strikeLayoff',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
  },
  {
    code: 'Return of Premium',
    propertyName: 'returnOfPremium',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
  },
];

export const groupRiderCodes2 = [
  {
    code: 'GVSP2',
    propertyName: 'initial',
    displayName: 'Initial/1st day',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'GVSHOSP',
    propertyName: 'dailyHospitalization',
    displayName: 'Daily Hospitalization',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
  {
    code: 'GVSICU',
    propertyName: 'icu',
    displayName: 'Intensive Care',
    units: [0.25, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
  },
];

export const isShop1 = (plan: string) => {
  return plan === 'GVSP1' ? true : false;
};
